import { datadogRum } from '@datadog/browser-rum';

import { getCreationSource } from './creation-source';

interface User {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  ownerOrganization?: {
    _id?: string;
    name?: string;
  };
  organizationId?: string;
  roles?: string[];
  permissions?: string[];
  oktaUserId?: string;
}

export const hasValidDatadogRumEnvironmentVariables = () => {
  if (!process.env.ENABLE_DATADOG_RUM) {
    return false;
  }

  if (
    !process.env.DATADOG_RUM_APPLICATION_ID ||
    !process.env.DATADOG_RUM_CLIENT_TOKEN
  ) {
    // eslint-disable-next-line no-console
    console.warn('Datadog RUM: Missing required environment variables');
    return false;
  }

  return true;
};

/**
 * Initialize Datadog RUM with the provided configuration
 */
export const initializeDatadogRum = () => {
  if (!hasValidDatadogRumEnvironmentVariables()) {
    return;
  }

  datadogRum.init({
    applicationId: process.env.DATADOG_RUM_APPLICATION_ID!,
    clientToken: process.env.DATADOG_RUM_CLIENT_TOKEN!,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: 'marley-chat',
    // from app/src/helpers/pend/pendo.js
    env: process.env.MARLEY_ACTIVE_ACCOUNT,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.DD_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100, // record 100% of sessions
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    enablePrivacyForActionName: true,
    defaultPrivacyLevel: 'allow', // allow by default and mask components using data-dd-privacy="hidden"
    allowFallbackToLocalStorage: true,
  });
};

/**
 * Setup Datadog RUM user and context
 * @param user - The user object containing user information
 */
export const setupDatadogRumUserAndContext = (user: User): void => {
  if (!hasValidDatadogRumEnvironmentVariables()) {
    return;
  }
  // Set user identification for Datadog RUM
  if (user._id) {
    const userName =
      user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.email;

    datadogRum.setUser({
      id: user._id,
      name: userName,
      email: user.email,
    });
  }

  // Set global context for filtering and analysis
  const globalContext = {
    organization_id: user.ownerOrganization?._id || user.organizationId,
    organization_name: user.ownerOrganization?.name,
    marley_active_account: process.env.MARLEY_ACTIVE_ACCOUNT,
    user_roles: user.roles,
    user_permissions: user.permissions,
    okta_user_id: user.oktaUserId,
    platform: getCreationSource(),
  };

  // Filter out undefined values
  const filteredContext = Object.fromEntries(
    Object.entries(globalContext).filter(([, value]) => value !== undefined),
  );

  datadogRum.setGlobalContext(filteredContext);
};

const datadogRumActions = {
  OKTA_LOGIN_SUCCESS: 'okta_login_success',
  OKTA_LOGIN_FAILURE: 'okta_login_failure',
};

export { datadogRum, datadogRumActions };
